import type { MessageView, PersonView, PostMessage, ProjectRole } from "@walkie/protocol"
import type {
	ProjectCard,
	AppConfig,
	ConnectApp,
	ConnectCode,
	ConnectCodeState,
	DevicesResponse,
	GithubSettings,
	Me,
	Metrics,
	PairInfo,
	ResolveInput,
	Snapshot,
	TeamMember,
} from "./types"

export class ApiError extends Error {
	constructor(
		public status: number,
		public code: string,
		message: string,
	) {
		super(message)
	}
}

export type Transport = (
	method: string,
	path: string,
	body?: unknown,
	token?: string,
) => Promise<unknown>

let getToken: () => Promise<string> = async () => {
	throw new ApiError(401, "unauthorized", "Not signed in")
}
let onUnauthorized: () => void = () => {}

/** Wired by the AuthProvider. Tokens only ever travel in the Authorization header. */
export function configureApi(opts: {
	getToken: () => Promise<string>
	onUnauthorized: () => void
}) {
	getToken = opts.getToken
	onUnauthorized = opts.onUnauthorized
}

const httpTransport: Transport = async (method, path, body, token) => {
	const headers: Record<string, string> = {}
	if (token) headers.authorization = `Bearer ${token}`
	if (body !== undefined) headers["content-type"] = "application/json"
	const res = await fetch(path, {
		method,
		headers,
		body: body === undefined ? undefined : JSON.stringify(body),
	})
	const text = await res.text()
	let data: unknown = null
	try {
		data = text ? JSON.parse(text) : null
	} catch {
		data = null
	}
	if (!res.ok) {
		const d = (data ?? {}) as { error?: string; message?: string }
		throw new ApiError(
			res.status,
			d.error ?? "error",
			d.message ?? `${res.status} ${res.statusText}`,
		)
	}
	return data
}

let transport: Transport = httpTransport
/** Fixtures mode swaps in an in-memory backend. */
export function setTransport(t: Transport) {
	transport = t
}

export async function publicGet<T>(path: string): Promise<T> {
	return (await transport("GET", path)) as T
}
export async function publicPost<T>(path: string, body: unknown): Promise<T> {
	return (await transport("POST", path, body)) as T
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
	const token = await getToken()
	try {
		return (await transport(method, `/api/w${path}`, body, token)) as T
	} catch (e) {
		if (e instanceof ApiError && e.status === 401) onUnauthorized()
		throw e
	}
}

const q = encodeURIComponent

export const api = {
	config: () => publicGet<AppConfig>("/api/config"),
	me: () => call<Me>("GET", "/me"),
	overview: () => call<{ projects: ProjectCard[] }>("GET", "/overview"),
	createTeam: (name: string, project?: string, access?: "team" | "restricted") =>
		call<{ teamId: string; projectId: string | null }>("POST", "/teams", {
			name,
			project,
			access,
		}),
	createProject: (
		teamId: string,
		name: string,
		opts: { description?: string; access?: "team" | "restricted" } = {},
	) =>
		call<{ projectId: string }>("POST", `/teams/${q(teamId)}/projects`, { name, ...opts }),
	removeTeamMember: (teamId: string, userId: string) =>
		call<{ ok: true }>("DELETE", `/teams/${q(teamId)}/members/${q(userId)}`),
	teamMembers: (teamId: string) =>
		call<{ members: TeamMember[] }>("GET", `/teams/${q(teamId)}/members`),
	invite: (
		teamId: string,
		body: {
			email?: string
			teamRole?: "admin" | "member"
			projectRoles?: Record<string, ProjectRole>
		},
	) => call<{ code: string; url: string }>("POST", `/teams/${q(teamId)}/invites`, body),
	acceptInvite: (code: string) =>
		call<{ teamId: string; projectId?: string | null }>("POST", "/invites/accept", { code }),

	snapshot: (pid: string) => call<Snapshot>("GET", `/projects/${q(pid)}/snapshot`),
	people: (pid: string) =>
		call<{ people: PersonView[] }>("GET", `/projects/${q(pid)}/people`),
	thread: (pid: string, rootId: string) =>
		call<{ messages: MessageView[] }>(
			"GET",
			`/projects/${q(pid)}/messages?thread=${q(rootId)}`,
		),
	older: (pid: string, beforeSeq: number) =>
		call<{ messages: MessageView[] }>(
			"GET",
			`/projects/${q(pid)}/messages?before=${beforeSeq}`,
		),
	search: (pid: string, text: string, limit = 50) =>
		call<{ messages: MessageView[] }>(
			"GET",
			`/projects/${q(pid)}/search?q=${q(text)}&limit=${limit}`,
		),
	post: (pid: string, body: Partial<PostMessage> & { body: string }) =>
		call<{ message: MessageView; created: boolean }>(
			"POST",
			`/projects/${q(pid)}/messages`,
			body,
		),
	/** Author or project admin. Answers with the tombstone (or nothing); a message_updated frame follows. */
	withdraw: (pid: string, mid: string) =>
		call<{ message?: MessageView } | null>(
			"POST",
			`/projects/${q(pid)}/messages/${q(mid)}/withdraw`,
			{},
		),
	claim: (pid: string, mid: string, body: { note?: string; eta?: string } = {}) =>
		call<{ message: MessageView }>(
			"POST",
			`/projects/${q(pid)}/requests/${q(mid)}/claim`,
			body,
		),
	release: (pid: string, mid: string) =>
		call<{ message: MessageView }>(
			"POST",
			`/projects/${q(pid)}/requests/${q(mid)}/release`,
			{},
		),
	resolve: (pid: string, mid: string, body: ResolveInput) =>
		call<{ message: MessageView }>(
			"POST",
			`/projects/${q(pid)}/requests/${q(mid)}/resolve`,
			body,
		),
	patchAgent: (
		pid: string,
		aid: string,
		body: { handle?: string; shared?: boolean; ended?: boolean; autoResume?: boolean },
	) =>
		call<{ agent: import("@walkie/protocol").AgentView }>(
			"PATCH",
			`/projects/${q(pid)}/agents/${q(aid)}`,
			body,
		),
	createRoom: (pid: string, name: string, topic?: string) =>
		call<{ roomId: string }>("POST", `/projects/${q(pid)}/rooms`, { name, topic }),
	createDm: (pid: string, agents: string[], users: string[]) =>
		call<{ roomId: string }>("POST", `/projects/${q(pid)}/dms`, { agents, users }),
	markRead: (pid: string, roomId: string, seq: number) =>
		call<{ ok: true }>("POST", `/projects/${q(pid)}/read`, { roomId, seq }),
	metrics: (pid: string) => call<Metrics>("GET", `/projects/${q(pid)}/metrics`),
	setProjectRole: (pid: string, userId: string, role: ProjectRole | null) =>
		call<{ ok: true }>("PUT", `/projects/${q(pid)}/members/${q(userId)}`, { role }),
	deleteProject: (pid: string) =>
		call<{ connections: number }>("DELETE", `/projects/${q(pid)}`),
	github: (pid: string) => call<GithubSettings>("GET", `/projects/${q(pid)}/github`),
	/** Asks one of the viewer's online computers to create the webhook with its `gh`. */
	githubSetup: (pid: string, repo: string, deviceId?: string) =>
		call<{ sent: boolean; deviceName?: string }>("POST", `/projects/${q(pid)}/github/setup`, {
			repo,
			deviceId,
		}),
	patchProject: (
		pid: string,
		body: {
			name?: string
			description?: string | null
			access?: "team" | "restricted"
			githubRepos?: string[]
			githubLogins?: Record<string, string>
		},
	) => call<{ ok: true }>("PATCH", `/projects/${q(pid)}`, body),

	devices: () => call<DevicesResponse>("GET", "/devices"),
	connectCode: (pid: string, body: { app: ConnectApp; path?: string }) =>
		call<ConnectCode>("POST", `/projects/${q(pid)}/connect-codes`, body),
	connectCodeStatus: (id: string) =>
		call<ConnectCodeState>("GET", `/connect-codes/${q(id)}`),
	connect: (
		deviceId: string,
		body: {
			projectId: string
			path?: string
			repoKey?: string
			apps: string[]
			autoShare: boolean
		},
	) =>
		call<{ connectionId: string; delivered: boolean }>(
			"POST",
			`/devices/${q(deviceId)}/connections`,
			body,
		),
	removeDevice: (id: string) => call<{ ok: true }>("DELETE", `/devices/${q(id)}`),
	updateDevice: (id: string) =>
		call<{ sent: boolean }>("POST", `/devices/${q(id)}/update`, {}),
	setAutoShare: (connId: string, autoShare: boolean) =>
		call<{ ok: true }>("PATCH", `/connections/${q(connId)}`, { autoShare }),
	repair: (connId: string) =>
		call<{ sent: boolean }>("POST", `/connections/${q(connId)}/repair`, {}),
	disconnect: (connId: string) =>
		call<{ ok: true }>("DELETE", `/connections/${q(connId)}`),
	pairInfo: (code: string) => call<PairInfo>("GET", `/pair/${q(code)}`),
	approvePair: (code: string) => call<{ ok: true }>("POST", "/pair/approve", { code }),
}

export function errorText(e: unknown): string {
	if (e instanceof ApiError) return e.message
	if (e instanceof Error) return e.message
	return String(e)
}
